import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNotify } from "react-admin";
import { useClub } from "../club/ClubContext";
import { httpClient } from "../httpClient";
import {
  Box, Typography, Card, CardContent, Button, Autocomplete, TextField,
  IconButton, Tooltip, Avatar, Chip, CircularProgress, Stack, Divider, InputAdornment,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";

interface NotificationType {
  type: string;
  label: string;
}

interface Recipient {
  id: number;
  club_id: number;
  notification_type: string;
  user_id: number;
  user_display_name?: string | null;
  user_email?: string | null;
  created_at: string;
}

interface ClubMember {
  id: number;
  display_name: string;
  email: string;
  avatar_url?: string | null;
}

interface AttendanceReminderSettings {
  attendance_response_due_hours: number;
  attendance_maybe_followup_hours: number;
}

function AttendanceReminderSettingsCard({ clubId }: { clubId: number | null }) {
  const notify = useNotify();
  const [settings, setSettings] = useState<AttendanceReminderSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!clubId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { json } = await httpClient(`/api/clubs/${clubId}/notification-recipients/settings`);
      setSettings(json);
    } catch {
      notify("Fehler beim Laden der Erinnerungs-Einstellungen", { type: "error" });
    } finally {
      setLoading(false);
    }
  }, [clubId, notify]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const { json } = await httpClient(`/api/clubs/${clubId}/notification-recipients/settings`, {
        method: "PUT",
        body: JSON.stringify(settings),
      });
      setSettings(json);
      notify("Gespeichert");
    } catch (e: any) {
      notify(e?.body?.detail ?? "Fehler beim Speichern", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent><CircularProgress size={20} /></CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography fontWeight={700} sx={{ mb: 0.5 }}>Termin-Zusage-Erinnerungen</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Legt fest, wie viele Stunden vor einem Termin die automatischen Erinnerungen an die Mitglieder verschickt werden.
        </Typography>

        <Stack spacing={2.5}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.25 }}>
              1. Zusage fehlt noch
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Wird an alle zugeordneten Mitglieder verschickt, die noch nicht mit Zusage/Absage/Vielleicht reagiert haben.
            </Typography>
            <TextField
              type="number"
              size="small"
              sx={{ maxWidth: 220 }}
              inputProps={{ min: 1, max: 720 }}
              value={settings.attendance_response_due_hours}
              onChange={e => setSettings({ ...settings, attendance_response_due_hours: Number(e.target.value) })}
              InputProps={{ endAdornment: <InputAdornment position="end">Stunden vorher</InputAdornment> }}
            />
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.25 }}>
              2. Status "Vielleicht" bestätigen
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Wird an Mitglieder verschickt, die mit "Vielleicht" geantwortet haben, damit sie ihre Teilnahme bestätigen.
            </Typography>
            <TextField
              type="number"
              size="small"
              sx={{ maxWidth: 220 }}
              inputProps={{ min: 1, max: 720 }}
              value={settings.attendance_maybe_followup_hours}
              onChange={e => setSettings({ ...settings, attendance_maybe_followup_hours: Number(e.target.value) })}
              InputProps={{ endAdornment: <InputAdornment position="end">Stunden vorher</InputAdornment> }}
            />
          </Box>

          <Box>
            <Button variant="contained" size="small" disabled={saving} onClick={handleSave}>
              {saving ? <CircularProgress size={16} /> : "Speichern"}
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function NotificationTypeCard({
  type, recipients, members, onAdd, onRemove,
}: {
  type: NotificationType;
  recipients: Recipient[];
  members: ClubMember[];
  onAdd: (notificationType: string, userId: number) => Promise<void>;
  onRemove: (recipient: Recipient) => Promise<void>;
}) {
  const [picked, setPicked] = useState<ClubMember | null>(null);
  const [busy, setBusy] = useState(false);

  const alreadyAdded = useMemo(
    () => new Set(recipients.map(r => r.user_id)),
    [recipients]
  );
  const options = useMemo(
    () => members.filter(m => !alreadyAdded.has(m.id)),
    [members, alreadyAdded]
  );

  const handleAdd = async () => {
    if (!picked) return;
    setBusy(true);
    try {
      await onAdd(type.type, picked.id);
      setPicked(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography fontWeight={700} sx={{ mb: 1 }}>{type.label}</Typography>

        {recipients.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Niemand ist für diese Benachrichtigung eingetragen.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
            {recipients.map(r => (
              <Chip
                key={r.id}
                label={r.user_display_name || r.user_email || `#${r.user_id}`}
                onDelete={() => onRemove(r)}
                deleteIcon={
                  <Tooltip title="Entfernen">
                    <DeleteOutlineIcon />
                  </Tooltip>
                }
              />
            ))}
          </Box>
        )}

        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Autocomplete
            size="small"
            sx={{ minWidth: 280, flexGrow: 1 }}
            options={options}
            getOptionLabel={o => `${o.display_name} (${o.email})`}
            value={picked}
            onChange={(_, v) => setPicked(v)}
            renderOption={(props, o) => (
              <Box component="li" {...props} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Avatar src={o.avatar_url ?? undefined} sx={{ width: 24, height: 24, fontSize: 11 }}>
                  {o.display_name?.[0]}
                </Avatar>
                <Box>
                  <Typography variant="body2">{o.display_name}</Typography>
                  <Typography variant="caption" color="text.secondary">{o.email}</Typography>
                </Box>
              </Box>
            )}
            renderInput={params => <TextField {...params} label="Mitglied hinzufügen" />}
          />
          <Button
            variant="contained"
            size="small"
            disabled={!picked || busy}
            onClick={handleAdd}
          >
            {busy ? <CircularProgress size={16} /> : "Hinzufügen"}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function ClubNotificationRecipientsPage() {
  const { clubId } = useClub();
  const notify = useNotify();
  const [types, setTypes] = useState<NotificationType[]>([]);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [members, setMembers] = useState<ClubMember[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!clubId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [typesRes, recipientsRes, membersRes] = await Promise.all([
        httpClient(`/api/clubs/${clubId}/notification-recipients/types`),
        httpClient(`/api/clubs/${clubId}/notification-recipients`),
        httpClient(`/api/users?clubId=${clubId}&perPage=500`),
      ]);
      setTypes(typesRes.json ?? []);
      setRecipients(recipientsRes.json ?? []);
      setMembers(membersRes.json?.data ?? []);
    } catch {
      notify("Fehler beim Laden", { type: "error" });
    } finally {
      setLoading(false);
    }
  }, [clubId, notify]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (notificationType: string, userId: number) => {
    try {
      await httpClient(`/api/clubs/${clubId}/notification-recipients`, {
        method: "POST",
        body: JSON.stringify({ notification_type: notificationType, user_id: userId }),
      });
      notify("Empfänger hinzugefügt");
      load();
    } catch (e: any) {
      notify(e?.body?.detail ?? "Fehler beim Hinzufügen", { type: "error" });
    }
  };

  const handleRemove = async (recipient: Recipient) => {
    try {
      await httpClient(`/api/clubs/${clubId}/notification-recipients/${recipient.id}`, { method: "DELETE" });
      notify("Empfänger entfernt");
      load();
    } catch {
      notify("Fehler beim Entfernen", { type: "error" });
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>;

  return (
    <Box sx={{ maxWidth: 700, mx: "auto", p: { xs: 1, sm: 2, md: 3 } }}>
      <Typography variant="h5" fontWeight={700} display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
        <NotificationsActiveIcon /> Benachrichtigungen
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Lege fest, wer im Verein über welche Ereignisse informiert wird (In-App, E-Mail und Push).
      </Typography>

      <AttendanceReminderSettingsCard clubId={clubId} />

      {types.map(t => (
        <NotificationTypeCard
          key={t.type}
          type={t}
          recipients={recipients.filter(r => r.notification_type === t.type)}
          members={members}
          onAdd={handleAdd}
          onRemove={handleRemove}
        />
      ))}
    </Box>
  );
}
